import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Stack } from '@mui/material'
import { shareCarePersonProfile } from 'api/carePersons'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, minButtonWidth } from 'const'
import { UIModal } from 'features/UI'
import React, { useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { object, string, TypeOf } from 'zod'
import FormInput from '../form-elements/FormInput'
import NotificationSys from '../NotificationSystem'

interface Props {
  isOpen: boolean
  handleClose: () => void
}

const formSchema = object({
  email: string().min(1, FIELD_IS_REQUIRED).email(EMAIL_IS_INVALID),
  message: string().optional(),
})

type FormType = TypeOf<typeof formSchema>

const ModalContent = ({ handleClose }: { handleClose: () => void }) => {
  const formSettings = useMemo(
    () => ({
      mode: 'onChange' as const,
      resolver: zodResolver(formSchema),
      defaultValues: {
        email: '',
        message: '',
      },
    }),
    [],
  )

  const formProps = useForm<FormType>(formSettings)
  const { handleSubmit, formState } = formProps

  const shareProfileMutation = useMutation(shareCarePersonProfile, {
    onSuccess: (_, { email }) => {
      handleClose()
      NotificationSys.showSuccess(`The summary has been sent to ${email}.`)
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = ({ email, message }: FormType) => {
    shareProfileMutation.mutate({ email, message })
  }

  return (
    <FormProvider {...formProps}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
        <Stack spacing={2}>
          <FormInput label="Email Address" name="email" required />
          <FormInput label="Message" name="message" minRows={3} multiline />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <LoadingButton variant="outlined" onClick={handleClose} sx={minButtonWidth}>
            Close
          </LoadingButton>
          <LoadingButton
            loading={shareProfileMutation.isLoading}
            disabled={!formState.isValid}
            variant="contained"
            type="submit"
            sx={minButtonWidth}
          >
            Send
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}

const ShareProfileModal = ({ isOpen, handleClose }: Props) => (
  <UIModal title="Share via Email" isOpen={isOpen} onClose={handleClose}>
    <ModalContent handleClose={handleClose} />
  </UIModal>
)

export default ShareProfileModal
